import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Alert, AlertDescription } from '../ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import {
  useCreateRentTerm,
  useRentTermPreview,
  useUpdateRentTerm,
  type LeaseRentPackDefault,
  type LeaseRentTerm,
  type LeaseRentTermInput,
} from '../../api/leaseRent'
import { useFormatters } from '../../lib/format'

export const FREQUENCIES = ['weekly', 'four_weekly', 'monthly', 'quarterly', 'annual'] as const
export const CHARGES_MODES = ['provision', 'flat_fee', 'included', 'none'] as const
const PRORATIONS = ['actual_days', 'thirty_day_month', 'whole_period'] as const

export interface TermDraft {
  effective_from: string
  rent_minor?: number | null
  currency?: string
  charges_minor?: number | null
  charges_mode?: string
  frequency?: string
  rent_free_from?: string | null
  rent_free_to?: string | null
  term?: string
  period_anchor?: string
  due_day?: number | null
  proration_rule?: string | null
}

interface Props {
  org: string
  contractUid: string
  /** The row being edited, or null for a new one. */
  term: LeaseRentTerm | null
  /** Initial values of a new row (prefill, never guessed). */
  draft?: TermDraft
  /** The default of the lease's jurisdiction: proposed on a click, never applied alone. */
  packDefault?: LeaseRentPackDefault | null
  currencyProposal?: string
  isFirst: boolean
  onClose: () => void
}

const NONE = '__none__'

export const toMinor = (v: string): number | null => {
  if (v.trim() === '') return null
  const n = Number(v.replace(',', '.'))
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null
}
const fromMinor = (m: number | null | undefined) => (m === null || m === undefined ? '' : (m / 100).toFixed(2))

/** Add and edit dialog of a rent term, with the due and proration editor and its live preview. */
export function RentTermDialog({ org, contractUid, term, draft, packDefault, currencyProposal, isFirst, onClose }: Props) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const create = useCreateRentTerm()
  const update = useUpdateRentTerm()
  const base: TermDraft = term
    ? {
        effective_from: term.effective_from,
        rent_minor: term.rent_minor,
        currency: term.currency,
        charges_minor: term.charges_minor,
        charges_mode: term.charges_mode,
        frequency: term.frequency,
        rent_free_from: term.rent_free_from,
        rent_free_to: term.rent_free_to,
        term: term.due_rule?.term,
        period_anchor: term.due_rule?.period_anchor,
        due_day: term.due_rule?.due_day,
        proration_rule: term.proration_rule,
      }
    : (draft ?? { effective_from: '' })

  const [effectiveFrom, setEffectiveFrom] = useState(base.effective_from ?? '')
  const [amount, setAmount] = useState(fromMinor(base.rent_minor))
  const [currency, setCurrency] = useState(base.currency ?? currencyProposal ?? '')
  const [chargesMode, setChargesMode] = useState(base.charges_mode ?? 'none')
  const [charges, setCharges] = useState(fromMinor(base.charges_minor))
  const [frequency, setFrequency] = useState(base.frequency ?? 'monthly')
  const [freeFrom, setFreeFrom] = useState(base.rent_free_from ?? '')
  const [freeTo, setFreeTo] = useState(base.rent_free_to ?? '')
  const [dueTerm, setDueTerm] = useState(base.term ?? '')
  const [anchor, setAnchor] = useState(base.period_anchor ?? '')
  const [dueDay, setDueDay] = useState(base.due_day ? String(base.due_day) : '')
  const [proration, setProration] = useState(base.proration_rule ?? '')
  const [error, setError] = useState<string[]>([])

  useEffect(() => setError([]), [effectiveFrom, amount, frequency])

  const needsChargesAmount = chargesMode === 'provision' || chargesMode === 'flat_fee'
  const hasDay = frequency !== 'weekly' && frequency !== 'four_weekly'
  const lockedByReview = term?.source === 'review'
  const dueComplete = dueTerm !== '' && anchor !== '' && proration !== ''

  const previewQuery = useMemo(() => {
    if (!effectiveFrom || !dueComplete) return null
    return {
      effective_from: effectiveFrom,
      frequency,
      rent_minor: toMinor(amount) ?? undefined,
      charges_minor: needsChargesAmount ? (toMinor(charges) ?? undefined) : undefined,
      term: dueTerm,
      period_anchor: anchor,
      due_day: hasDay && dueDay ? Number(dueDay) : undefined,
      proration_rule: proration,
      rent_free_from: freeFrom || undefined,
      rent_free_to: freeTo || undefined,
    }
  }, [effectiveFrom, dueComplete, frequency, amount, charges, needsChargesAmount, dueTerm, anchor, hasDay, dueDay, proration, freeFrom, freeTo])
  const preview = useRentTermPreview(org, contractUid, previewQuery)

  const usePackDefault = () => {
    if (!packDefault) return
    const rule = packDefault.due_rule
    if (rule) {
      setDueTerm(rule.term)
      setAnchor(rule.period_anchor)
      setDueDay(rule.due_day ? String(rule.due_day) : '')
    }
    if (packDefault.proration_rule) setProration(packDefault.proration_rule)
  }

  const save = async () => {
    const rent = toMinor(amount)
    const input: LeaseRentTermInput = {
      effective_from: effectiveFrom,
      rent_minor: rent ?? undefined,
      currency: currency.trim().toUpperCase(),
      charges_mode: chargesMode as LeaseRentTermInput['charges_mode'],
      charges_minor: needsChargesAmount ? toMinor(charges) : null,
      frequency: frequency as LeaseRentTermInput['frequency'],
      due_rule: dueComplete || dueTerm || anchor
        ? ({ term: dueTerm, period_anchor: anchor, ...(hasDay && dueDay ? { due_day: Number(dueDay) } : {}) } as LeaseRentTermInput['due_rule'])
        : null,
      proration_rule: (proration || null) as LeaseRentTermInput['proration_rule'],
      rent_free_from: freeFrom || null,
      rent_free_to: freeTo || null,
    }
    try {
      if (term) {
        await update.mutateAsync({ params: { path: { orgCode: org, contractUid, termUid: term.uid } }, body: input })
      } else {
        input.source = isFirst ? 'lease' : 'amendment'
        await create.mutateAsync({ params: { path: { orgCode: org, contractUid } }, body: input })
      }
      onClose()
    } catch (e) {
      const problem = e as { title?: string; fields?: { name: string; message: string }[] }
      const msgs = (problem.fields ?? []).map((f) => `${f.name}: ${f.message}`)
      setError(msgs.length ? msgs : [problem.title ?? t('lease.rent_schedule.save_failed')])
    }
  }

  const sel = (value: string, set: (v: string) => void, options: readonly string[], prefix: string, testId: string, allowNone = false) => (
    <Select value={value || NONE} onValueChange={(v) => set(v === NONE ? '' : v)}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value={NONE}>{t('lease.rent_schedule.unset')}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testId}-${o}`}>{t(`lease.rent_schedule.${prefix}.${o}` as any)}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rent-term-dialog">
        <DialogHeader>
          <DialogTitle>{term ? t('lease.rent_schedule.edit_title') : t('lease.rent_schedule.add_title')}</DialogTitle>
          <DialogDescription>{t('lease.rent_schedule.dialog_help')}</DialogDescription>
        </DialogHeader>

        {lockedByReview && (
          <Alert><AlertDescription>{t('lease.rent_schedule.review_locked')}</AlertDescription></Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="rent-term-from">{t('lease.rent_schedule.effective_from')}</Label>
            <Input id="rent-term-from" type="date" value={effectiveFrom} disabled={lockedByReview}
              onChange={(e) => setEffectiveFrom(e.target.value)} data-testid="rent-term-from-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-term-amount">{t('lease.rent_schedule.rent')}</Label>
            <Input id="rent-term-amount" inputMode="decimal" value={amount} disabled={lockedByReview}
              onChange={(e) => setAmount(e.target.value)} data-testid="rent-term-amount-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-term-currency">{t('lease.rent_schedule.currency')}</Label>
            <Input id="rent-term-currency" maxLength={3} value={currency}
              onChange={(e) => setCurrency(e.target.value)} data-testid="rent-term-currency-input" />
          </div>
          <div className="space-y-1">
            <Label>{t('lease.rent_schedule.frequency')}</Label>
            {sel(frequency, setFrequency, FREQUENCIES, 'frequencies', 'rent-term-frequency-select')}
          </div>
          <div className="space-y-1">
            <Label>{t('lease.rent_schedule.charges_mode')}</Label>
            {sel(chargesMode, setChargesMode, CHARGES_MODES, 'charges_modes', 'rent-term-charges-mode-select')}
          </div>
          {needsChargesAmount && (
            <div className="space-y-1">
              <Label htmlFor="rent-term-charges">{t('lease.rent_schedule.charges')}</Label>
              <Input id="rent-term-charges" inputMode="decimal" value={charges}
                onChange={(e) => setCharges(e.target.value)} data-testid="rent-term-charges-input" />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="rent-term-free-from">{t('lease.rent_schedule.free_from')}</Label>
            <Input id="rent-term-free-from" type="date" value={freeFrom}
              onChange={(e) => setFreeFrom(e.target.value)} data-testid="rent-term-free-from-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rent-term-free-to">{t('lease.rent_schedule.free_to')}</Label>
            <Input id="rent-term-free-to" type="date" value={freeTo}
              onChange={(e) => setFreeTo(e.target.value)} data-testid="rent-term-free-to-input" />
          </div>
        </div>

        <div className="space-y-3 rounded-lg border border-border p-3" data-testid="due-rule-editor">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">{t('lease.rent_schedule.due_rule')}</h3>
            {packDefault && (
              <Button type="button" variant="outline" size="sm" onClick={usePackDefault} data-testid="due-rule-pack-default-button">
                {t('lease.rent_schedule.use_pack_default')}
              </Button>
            )}
          </div>
          {packDefault?.legal_basis && (
            <p className="text-xs text-muted-foreground" data-testid="due-rule-legal-basis">{packDefault.legal_basis}</p>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>{t('lease.rent_schedule.due_term')}</Label>
              {sel(dueTerm, setDueTerm, ['in_advance', 'in_arrears'], 'terms', 'due-rule-term-select', true)}
            </div>
            <div className="space-y-1">
              <Label>{t('lease.rent_schedule.due_anchor')}</Label>
              {sel(anchor, setAnchor, ['calendar', 'lease_start'], 'anchors', 'due-rule-anchor-select', true)}
            </div>
            {hasDay && (
              <div className="space-y-1">
                <Label htmlFor="due-rule-day">{t('lease.rent_schedule.due_day')}</Label>
                <Input id="due-rule-day" type="number" min={1} max={31} value={dueDay}
                  onChange={(e) => setDueDay(e.target.value)} data-testid="due-rule-day-input" />
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('lease.rent_schedule.proration')}</Label>
            {sel(proration, setProration, PRORATIONS, 'prorations', 'proration-rule-select', true)}
          </div>

          <div className="rounded bg-muted/50 p-2 text-sm" data-testid="due-rule-preview">
            {!previewQuery && <span className="text-muted-foreground">{t('lease.rent_schedule.preview_incomplete')}</span>}
            {previewQuery && preview.isError && <span className="text-destructive">{t('lease.rent_schedule.preview_failed')}</span>}
            {previewQuery && preview.data && (
              <div className="space-y-1">
                <div>
                  {t('lease.rent_schedule.preview_due')}{' '}
                  <span data-testid="due-rule-preview-dates">
                    {(preview.data.due_dates ?? []).map((d) => formatDate(d)).join(', ')}
                  </span>
                </div>
                {preview.data.first_period && preview.data.prorated && (
                  <div data-testid="due-rule-preview-first">
                    {t('lease.rent_schedule.preview_first', {
                      days: preview.data.first_period.days,
                      amount: formatMoney(preview.data.prorated.rent_minor, currency || null),
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {error.length > 0 && (
          <Alert variant="destructive" data-testid="rent-term-error">
            <AlertDescription>{error.map((m) => <div key={m}>{m}</div>)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="rent-term-cancel-button">{t('lease.rent_schedule.cancel')}</Button>
          <Button type="button" onClick={() => void save()} disabled={create.isPending || update.isPending} data-testid="rent-term-save-button">
            {t('lease.rent_schedule.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
